import { Image, Stack } from '@mantine/core'
import {
  IconArrowDown,
  IconArrowUp,
  IconCircleCheck,
  IconCircleMinus,
  IconCircleX,
  IconListNumbers,
} from '@tabler/icons-react'
import { z } from 'zod'
import { useCompetitionId } from '../../../../common/hooks'
import { getRankSuffix } from '../../../../common/helpers'
import { useTeamRanks, type TeamRank } from '../../../../ranks/api'
import { useCompetitionStats } from '../../../../ladder/api'
import { type LastFiveMatches } from '../../../../teams/api'
import { DivisionSelect, TitleInput } from './OptionsFormFields'
import { WidgetEmpty, WidgetSuspense } from './WidgetShell'
import classes from './Ladder.module.css'
import { DashboardWidget } from './types'

const TOP_N = 5

const Schema = z.object({
  title: z.string().default('Ladder'),
  division: z.number().nullable().default(null),
})

type Settings = z.infer<typeof Schema>

function ChangeIndicator({ change }: { change: number }) {
  if (change > 0) {
    return (
      <div className={`${classes.change} ${classes.changeUp}`}>
        <IconArrowUp size={24} stroke={2} />
        <span>+{change}</span>
      </div>
    )
  }
  if (change < 0) {
    return (
      <div className={`${classes.change} ${classes.changeDown}`}>
        <IconArrowDown size={24} stroke={2} />
        <span>{change}</span>
      </div>
    )
  }
  return (
    <div className={`${classes.change} ${classes.changeFlat}`}>
      <span>—</span>
    </div>
  )
}

function FormCell({ form }: { form: LastFiveMatches | undefined }) {
  if (!form || form.length === 0) {
    return <div className={classes.formCol} />
  }
  return (
    <div className={classes.formCol}>
      {form.map((result, i) => {
        if (result === 'win') {
          return (
            <span
              key={i}
              className={`${classes.formIcon} ${classes.formWin}`}
              aria-label="win"
            >
              <IconCircleCheck stroke={1.75} />
            </span>
          )
        }
        if (result === 'loss') {
          return (
            <span
              key={i}
              className={`${classes.formIcon} ${classes.formLoss}`}
              aria-label="loss"
            >
              <IconCircleX stroke={1.75} />
            </span>
          )
        }
        return (
          <span
            key={i}
            className={`${classes.formIcon} ${classes.formDraw}`}
            aria-label="draw"
          >
            <IconCircleMinus stroke={1.75} />
          </span>
        )
      })}
    </div>
  )
}

function LadderRow({
  team,
  rank,
  change,
  form,
}: {
  team: TeamRank
  rank: number
  change: number
  form: LastFiveMatches | undefined
}) {
  return (
    <div className={`${classes.row} ${classes.body}`}>
      <div className={classes.teamCol}>
        <div className={classes.rank}>
          {rank}
          <span className={classes.rankSuffix}>{getRankSuffix(rank)}</span>
        </div>
        <ChangeIndicator change={change} />
        <Image
          src={team.team_logo.thumbnail}
          alt={team.team_name}
          fit="contain"
          className={classes.logo}
        />
        <div className={classes.teamName}>{team.team_name}</div>
      </div>
      <div className={classes.statCol}>{team.rating.toFixed(2)}</div>
      <div className={classes.statCol}>{Math.round(team.points)}</div>
      <div className={classes.statCol}>{Math.round(team.round_points)}</div>
      <div className={classes.statCol}>{Math.round(team.month_points)}</div>
      <FormCell form={form} />
    </div>
  )
}

function Inner({ settings }: { settings: Settings }) {
  const competitionId = useCompetitionId()
  const divisionId = settings.division ?? undefined

  const { data: ranksResponse } = useTeamRanks({
    competition_id: competitionId,
    division_id: divisionId,
  })
  const { data: statsResponse } = useCompetitionStats({
    competition_id: competitionId,
    division: divisionId,
  })

  const formByTeam = new Map<number, LastFiveMatches>()
  for (const t of statsResponse.items) {
    formByTeam.set(t.team_id, t.last_5_matches)
  }

  const useDivisionRank = divisionId !== undefined
  const sorted = [...ranksResponse.items].sort((a, b) => {
    const ra = useDivisionRank ? a.division_rank : a.rank
    const rb = useDivisionRank ? b.division_rank : b.rank
    return ra - rb
  })
  const top = sorted.slice(0, TOP_N)

  if (top.length === 0) {
    return <WidgetEmpty message="No team ranks yet" />
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.title}>{settings.title}</div>
      <div className={classes.table}>
        <div className={`${classes.row} ${classes.header}`}>
          <div className={classes.teamCol}>
            <div className={classes.rank}>#</div>
            <div style={{ width: 55, flexShrink: 0 }} />
            <div style={{ width: 80, flexShrink: 0 }} />
            <div className={classes.teamName}>Team</div>
          </div>
          <div className={`${classes.statCol} ${classes.headerStatCol}`}>
            Rating
          </div>
          <div className={`${classes.statCol} ${classes.headerStatCol}`}>
            Total Pts
          </div>
          <div className={`${classes.statCol} ${classes.headerStatCol}`}>
            League Pts
          </div>
          <div className={`${classes.statCol} ${classes.headerStatCol}`}>
            KPI Pts
          </div>
          <div
            className={`${classes.formCol} ${classes.headerStatCol}`}
            style={{ justifyContent: 'center' }}
          >
            Recent Form
          </div>
        </div>
        {top.map((team) => {
          const rank = useDivisionRank ? team.division_rank : team.rank
          const change = useDivisionRank
            ? team.division_rank_change
            : team.rank_change
          return (
            <LadderRow
              key={team.team_id}
              team={team}
              rank={rank}
              change={change}
              form={formByTeam.get(team.team_id)}
            />
          )
        })}
      </div>
    </div>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSuspense>
      <Inner settings={settings} />
    </WidgetSuspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <DivisionSelect
        value={value.division}
        onChange={(v) => patch({ division: v })}
      />
    </Stack>
  )
}

export const LadderWidget: DashboardWidget<Settings> = {
  type: 'ladder',
  label: 'Ladder',
  description: 'Top 5 teams with rating and points',
  icon: IconListNumbers,
  minW: 4,
  minH: 3,
  maxW: 4,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
